import { DashboardAccountInfo, currencyFormat, numberFormat, mapPlaidAccountToNetWorthEntry } from "@ecountabl/lib"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { Animated, Pressable, StyleSheet, Text, TouchableOpacity, View } from "react-native"
import { Ionicons } from "@expo/vector-icons"
import { ThemeContext } from "./utility/context/ThemeContext"
import { ShadowView, textStyles } from "./style"
import { useAppSelector } from "./store"
import { LinearGradient } from 'expo-linear-gradient'
import Haptics from "./utility/haptics"

type Props = {
  account: DashboardAccountInfo,
  onMenuPress?: () => void
}

const AccountCard:React.FC<Props> = ({
  account, onMenuPress
}: Props) => {
  const theme = useContext(ThemeContext)
  const { borderColor, white } = theme.theme.colors

  const [showOptions, setShowOptions] = useState(false)

  const toggleWindow = () => {
    setShowOptions(!showOptions)
  }

  const handleRemove = () => {
    setShowOptions(false)
  }
  
  const labelAndValue = useMemo(() => {
    const netWorthEntry = mapPlaidAccountToNetWorthEntry(account)
    return {
      label: netWorthEntry.label,
      value: netWorthEntry.value
    }
  },[account])

  return (
    <ShadowView style={{borderRadius: 10}}>
      <Pressable
        disabled={account.fromCustomer || !showOptions}
        style={[
          styles.card,
          { backgroundColor: account.color }
        ]}
        onPress={toggleWindow}
      >
        <LinearGradient
          colors={["rgba(0,0,0,0)", "rgba(0,0,0,0.23)"]}
          style={StyleSheet.absoluteFillObject}
        />
        {
          onMenuPress ?
          <TouchableOpacity
            style={styles.iconWrapper}
            onPress={() => {
              Haptics.impactLight()
              onMenuPress()
            }}
          >
            <Ionicons name="ellipsis-horizontal" size={24} color={white} style={styles.ellipsis} selectable={false} />
          </TouchableOpacity> : null
        }
        <Text
          style={[
            textStyles.h2,
            { height: 60, paddingRight: 30, color: white, lineHeight: 30 }
          ]}
          numberOfLines={2}
          ellipsizeMode="tail"
        >
          {account.institution}
        </Text>
        <Text style={[textStyles.body, styles.accountName, {color: white}]}>
          {account.accountName}
        </Text>
        <View>
          <View>
            <Text style={[textStyles.body, styles.subheader, {color: white}]}>
              {labelAndValue.label}
            </Text>
            <Text style={[textStyles.body, styles.amount, {color: white}]}>
              {currencyFormat(labelAndValue.value)}
            </Text>
          </View>
          <View>
            <Text style={[textStyles.body, styles.subheader, {color: white}]}>
              Account Number
            </Text>
            <Text style={[textStyles.body, styles.accNumber, {color: white}]}>
              {account.accountNumber}
            </Text>
          </View>
        </View>
        {/* Options for account cards open on ellipsis press */}
        <View style={[styles.optionWindow, {display: showOptions ? "flex" : "none", backgroundColor: white, borderColor: borderColor}]}>
          <TouchableOpacity
            style={[styles.cardOption,{backgroundColor: white}]}
            onPress={() => {
              Haptics.notificationWarning()
              handleRemove()
            }}
          >
            <Ionicons name="close" size={15}/>
            <Text style={textStyles.body}>Remove</Text>
          </TouchableOpacity>
        </View>
        {/* APY info if account is meant to show it */}
        {
          account.fromCustomer && account.apy ?
          <AccountRateInformation account={account} />
          : null
        }
      </Pressable>
    </ShadowView>
    
  )
}

type AccountRateInformationProps = {
  account: DashboardAccountInfo
}

const AccountRateInformation = ({account}: AccountRateInformationProps) => {
  const theme = useContext(ThemeContext)
  const { white } = theme.theme.colors

  const {user: {customer}} = useAppSelector(state => state.auth)

  const ref = React.createRef<any>()
  const animatedOpacity = useRef(new Animated.Value(0)).current

  // add hover listeners to info tag
  useEffect(() => {
    const view = ref.current

    const handleHoverIn = () => {
      Animated.timing(animatedOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: false
      }).start()
    }
    const handleHoverOut = () => {
      Animated.timing(animatedOpacity, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false
      }).start()
    }

    view?.addEventListener('mouseenter', handleHoverIn)
    view?.addEventListener('mouseleave', handleHoverOut)

    return () => {
      view?.removeEventListener('mouseenter', handleHoverIn)
      view?.removeEventListener('mouseleave', handleHoverOut)
    }
  }, [])

  let productInterestRateMessage = ''

  const hasCustomRate = customer?.customProduct?.apy
  const hasAccountRate = account?.apy

  if (hasCustomRate && hasAccountRate) {
    const isBelowCustomRate = account.apy < customer.customProduct.apy
    const messaging = customer.customProduct.messaging

    productInterestRateMessage = isBelowCustomRate
      ? messaging.requirementsToEarnHighInterest
      : messaging.requirementsToKeepHighInterest
  }

  return (
  <View style={{ position: "absolute", bottom: 10, right: 10, width: "80%", height: "auto" }}>
    <Text style={[textStyles.body, styles.subheader, { color: white, textAlign: "right" }]} selectable={false}>
      APY
    </Text>
    <View>
      <View
        ref={ref}
        style={{
          flexDirection: "row",
          justifyContent: "flex-end",
          alignSelf: "flex-end",
          alignItems: "center",
        }}
      >
        <Text
          style={[
            textStyles.body,
            styles.accNumber,
            { color: white }
          ]}
          selectable={false}
        >
          {numberFormat(account.apy)}%
        </Text>
        <Ionicons
          name="information-circle"
          size={15}
          color={white}
          style={{ marginLeft: 4 }}
        />
      </View>
    </View>
    <Animated.View
      style={[
        styles.tooltip,
        {
          backgroundColor: white,
          opacity: animatedOpacity,
          marginTop: 4, // spacing from the above row
        }
      ]}
    >
      <Text
        style={[textStyles.subscript, { color: "black", textAlign: "left" }]}
        selectable={false}
      >
        {productInterestRateMessage}
      </Text>
    </Animated.View>
  </View>

  )
}

type DemoAccountCardProps = {
  color: string;
  institution: string;
  accountName: string;
}

const DemoAccountCard:React.FC<DemoAccountCardProps> = ({
  color, institution, accountName
}: DemoAccountCardProps) => {
  const theme = useContext(ThemeContext)
  const { white } = theme.theme.colors

  const [height, setHeight] = useState(0)

  const BULLET_CHAR = "\u2022"

  return (
    <ShadowView
      style={[
        styles.demoCard,
        { backgroundColor: color, width: "100%", minWidth: 250, maxWidth: 280, paddingHorizontal: 16, paddingVertical: 18 }
      ]}
      // on layout set height but consider verticalPadding which is 18
      onLayout={(event) => {setHeight(event.nativeEvent.layout.height - (18*2))}}
    >
      {/* Gradient background component */}
      <LinearGradient
        colors={["rgba(0,0,0,0)", "rgba(0,0,0,0.23)"]}
        style={StyleSheet.absoluteFillObject}
      />
      <Text
        style={[
          textStyles.h2,
          {
            fontSize: 20,
            minHeight: 50,
            paddingRight: 0,
            color: white
          }
        ]}
        numberOfLines={2}
        ellipsizeMode="tail"
      >
        {institution}
      </Text>
      <Text
        style={[
          textStyles.body,
          styles.accountName,
          { fontSize: Math.min(height * 0.07, 16), color: white}
        ]}
      >
        {accountName}
      </Text>
      <View>
        <View>
          <Text
            style={[
              textStyles.subscript,
              styles.subheader,
              {fontSize: Math.min(height * 0.07,16), color: white}
            ]}
          >
            Balance
          </Text>
          <Text
            style={[
              textStyles.subscript,
              styles.amount,
              {fontSize: Math.min(height * 0.07,16), color: white}
            ]}
          >
            {`$${new Array(6).fill(BULLET_CHAR).join("")}`}
          </Text>
        </View>
        <View>
          <Text
            style={[
              textStyles.subscript,
              styles.subheader,
              {fontSize: Math.min(height * 0.07,16), color: white}
            ]}
          >
            Account Number
          </Text>
          <Text
            style={[
              textStyles.subscript,
              styles.accNumber,
              {fontSize: Math.min(height * 0.07,16), color: white}
            ]}
          >
            {`${new Array(4).fill(BULLET_CHAR).join("")} ${new Array(4).fill(BULLET_CHAR).join("")}`}
          </Text>
        </View>
      </View>
    </ShadowView>
  )
}

export { AccountCard, DemoAccountCard }

const styles = StyleSheet.create({
  demoCard: {
    flexDirection: "column",
    justifyContent: "space-between",
    aspectRatio: 1.586,
    paddingVertical: 18,
    paddingHorizontal: 20,
    borderRadius: 10,
    overflow: "hidden",
  },
  card: {
    flexDirection: "column",
    justifyContent: "space-between",
    width: 350,
    aspectRatio: 1.586,
    paddingVertical: 20,
    paddingHorizontal: 24,
    maxHeight: 240,
    borderRadius: 10,
    overflow: "hidden",
  },
  ellipsis: {
    position: "absolute",
    right: 10,
    top: 10,
    padding: 3
  },
  iconWrapper: {
    position: 'absolute',
    right: 10,
    top: 10,
    zIndex: 1,
    cursor: "pointer",
  },
  instituionName: {
    paddingRight: 30,
    fontSize: 24,
    fontWeight: "700",
  },
  accountName: {
    fontWeight: "500",
  },
  infoContainer: {
    justifyContent: "space-between",
  },
  infoPair: {
    flex: 1
  },
  subheader: {
    fontSize: 11,
    opacity: 0.5,
  },
  amount: {
    fontSize: 17,
    fontWeight: "500",
    letterSpacing: 3,
  },
  accNumber: {
    fontSize: 14,
  },
  optionWindow: {
    borderWidth: 1,
    minWidth: 120,
    position: "absolute",
    right: 16,
    top: 50,
    paddingVertical: 8,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 10
  },
  cardOption: {
    width: "100%",
    paddingVertical: 9,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 6
  },
  errorCircle: {
    padding: 2,
    height: 20,
    width: 20,
    borderRadius: 100
  },
  tooltip: {
    position: "absolute",
    bottom: 40,
    right: 0,
    width: "100%",
    padding: 5,
    borderRadius: 5,
  }
})